// src/components/CreateTeamPage.js
import React, { useState, useContext, useEffect } from 'react';
import { AppContext } from '../App';
import authService from '../services/authService';
import config from '../config';
import { trackEvent } from '../analytics';

const CreateTeamPage = ({ onTeamCreated }) => {
  const { user } = useContext(AppContext);
  const [teamName, setTeamName] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [suggestedName, setSuggestedName] = useState('');
  const [password, setPassword] = useState('');
  const [email, setEmail] = useState('');
  const [isNewUser, setIsNewUser] = useState(false);

  // Generate a suggested team name based on the user's email domain
  useEffect(() => {
    if (user && user.email) {
      setEmail(user.email);
      try {
        const emailParts = user.email.split('@');
        if (emailParts.length > 1) {
          const domain = emailParts[1];
          const domainName = domain.split('.')[0];
          // Capitalize the first letter of the domain
          const suggestedTeamName = domainName.charAt(0).toUpperCase() + domainName.slice(1);
          setSuggestedName(suggestedTeamName);
          setTeamName(suggestedTeamName); // Pre-fill the input
        }
      } catch (error) {
        console.error('Error generating suggested team name:', error);
      }
    } else {
      // No user is logged in, they need to register
      setIsNewUser(true);
    }
  }, [user]);

  const handleCreateTeam = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!teamName.trim()) {
      setError('Team name is required');
      return;
    }
    
    if (isNewUser) {
      if (!email.trim()) {
        setError('Email is required');
        return;
      }
      
      if (!password.trim()) {
        setError('Password is required');
        return;
      }

      // Validate email format
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        setError('Please enter a valid email address');
        return;
      }

      // Validate password strength
      if (password.length < 6) {
        setError('Password must be at least 6 characters long');
        return;
      }
    }
    
    setIsLoading(true);
    
    try {
      if (isNewUser) {
        // For new users, register with team creation in a single step
        console.log('Creating new user with team:', { email, teamName });
        const result = await authService.register(email, password, teamName);
        
        if (result && result.success) {
          console.log('User and team created successfully');
          // Refresh the user data
          if (onTeamCreated) {
            await onTeamCreated();
          }
        } else {
          throw new Error('Failed to create user and team');
        }
      } else {
        // For existing users, create a team and associate it with the user
        console.log('Creating team for existing user');
        const response = await fetch(`${config.apiBaseUrl}/api/teams`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...authService.getAuthHeaders()
          },
          body: JSON.stringify({
            name: teamName
          })
        });
        
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to create team');
        }
        
        const data = await response.json();
        console.log('Team created successfully:', data);
        trackEvent('create_team', { during: 'existing_user' });

        // Refresh the user data to include the new team
        if (onTeamCreated) {
          await onTeamCreated();
        }
      }
    } catch (error) {
      console.error('Error creating team:', error);
      setError(error.message || 'An error occurred while creating the team');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="max-w-md mx-auto">
      <div className="bg-white/10 p-8 rounded-lg shadow-lg">
        <h1 className="text-2xl font-bold mb-6">Create Your Team</h1>
        
        <div className="mb-6">
          <p>
            Welcome to Táillí! To get started, you need to create a team. 
            Your team will contain all your API keys, products, and collaborators.
          </p>
        </div>
        
        {error && (
          <div className="mb-6 p-4 bg-red-500/20 text-red-200 rounded-lg">
            {error}
          </div>
        )}
        
        <form onSubmit={handleCreateTeam} className="space-y-6">
          {isNewUser && (
            <>
              <div>
                <label className="block text-sm font-medium mb-2">Email</label>
                <input
                  type="email"
                  className="w-full p-3 rounded bg-white/10 border border-white/20"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2">Password</label>
                <input
                  type="password"
                  className="w-full p-3 rounded bg-white/10 border border-white/20"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Create a password"
                  required
                />
              </div>
            </>
          )}
          
          <div>
            <label className="block text-sm font-medium mb-2">Team Name</label>
            <input
              type="text"
              className="w-full p-3 rounded bg-white/10 border border-white/20"
              value={teamName}
              onChange={(e) => setTeamName(e.target.value)}
              placeholder="Enter team name"
              required
            />
            
            {suggestedName && (
              <p className="mt-2 text-sm text-gray-300">
                We've suggested a name based on your email domain. Feel free to change it.
              </p>
            )}
          </div>
          
          <button
            type="submit"
            className="w-full py-3 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded transition-colors disabled:opacity-50"
            disabled={isLoading}
          >
            {isLoading ? (
              <span className="flex items-center justify-center">
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                {isNewUser ? 'Creating Account & Team...' : 'Creating Team...'}
              </span>
            ) : (
              isNewUser ? 'Create Account & Team' : 'Create Team'
            )}
          </button>
        </form>
        
        <div className="mt-8 border-t border-white/10 pt-6">
          <h2 className="text-lg font-semibold mb-3">Joining an existing team?</h2>
          <p className="text-sm mb-4">
            If you've been invited to join an existing team, ask your team admin for an invitation link.
          </p>
        </div>
      </div>
    </div>
  );
};

export default CreateTeamPage;